import React, { useState } from 'react'
import { motion } from 'framer-motion'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog'
import { Plus, Settings, Search } from 'lucide-react'
import { useEngineDomains, useCreateEngineDomain, useUpdateEngineDomain, useDeleteEngineDomain } from '@/features/domains/hooks'
import { useToast } from '@/hooks/use-toast'
import { ImportExportButtons } from '@/components/ui/import-export-buttons'
import { IconPicker } from '@/components/ui/icon-picker'
import { DomainCard, type DomainCardData } from './domain-card'
import { AuthorizationGate } from '@/features/authorization/AuthorizationGate'

interface Domain {
  id: string
  name: string
  description: string
  icon?: string
  pass_threshold?: number
  is_active: boolean
  question_count?: number
  created_at: string
  updated_at: string
}

const DomainManagement: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('')
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false)
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false)
  const [selectedDomain, setSelectedDomain] = useState<Domain | null>(null)
  const [formData, setFormData] = useState({
    id: '',
    name: '',
    description: '',
    icon: 'FileText',
    pass_threshold: 70,
  })

  const { toast } = useToast()

  // Fetch domains via hook
  const { data: domainsData, isLoading, error } = useEngineDomains({ per_page: 100 })

  // Mutation hooks
  const createDomainMutation = useCreateEngineDomain()
  const updateDomainMutation = useUpdateEngineDomain()
  const deleteDomainMutation = useDeleteEngineDomain()

  const resetForm = () => {
    setFormData({
      id: '',
      name: '',
      description: '',
      icon: 'FileText',
      pass_threshold: 70,
    })
  }

  const handleCreate = () => {
    const payload = { ...formData, pass_threshold: formData.pass_threshold / 100 }
    createDomainMutation.mutate(payload, {
      onSuccess: () => {
        setIsCreateDialogOpen(false)
        resetForm()
        toast({ title: 'Success', description: 'Domain created successfully' })
      },
      onError: (error: any) => {
        toast({ title: 'Error', description: error.response?.data?.error || 'Failed to create domain', variant: 'destructive' })
      }
    })
  }

  const handleEdit = (domain: Domain) => {
    setSelectedDomain(domain)
    setFormData({
      id: domain.id,
      name: domain.name,
      description: domain.description,
      icon: domain.icon || 'FileText',
      pass_threshold: Math.round((domain.pass_threshold ?? 0.70) * 100),
    })
    setIsEditDialogOpen(true)
  }

  const handleUpdate = () => {
    if (selectedDomain) {
      const payload = { ...formData, pass_threshold: formData.pass_threshold / 100 }
      updateDomainMutation.mutate({ id: selectedDomain.id, data: payload }, {
        onSuccess: () => {
          setIsEditDialogOpen(false)
          setSelectedDomain(null)
          resetForm()
          toast({ title: 'Success', description: 'Domain updated successfully' })
        },
        onError: (error: any) => {
          toast({ title: 'Error', description: error.response?.data?.error || 'Failed to update domain', variant: 'destructive' })
        }
      })
    }
  }

  const handleDelete = (id: string) => {
    deleteDomainMutation.mutate(id, {
      onSuccess: () => {
        toast({ title: 'Success', description: 'Domain deleted successfully' })
      },
      onError: (error: any) => {
        toast({ title: 'Error', description: error.response?.data?.error || 'Failed to delete domain', variant: 'destructive' })
      }
    })
  }

  if (isLoading) return <div>Loading...</div>
  if (error) return <div>Error loading domains</div>

  const domains = domainsData?.data || []
  const filteredDomains = domains.filter((domain: Domain) =>
    domain.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    domain.description.toLowerCase().includes(searchTerm.toLowerCase())
  )

  return (
    <motion.div
      className="space-y-6"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
    >
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">Domain Management</h3>
          <p className="text-sm text-muted-foreground">
            Manage IT domains for the recommendation engine
          </p>
        </div>
        <div className="flex items-center gap-3">
          <AuthorizationGate actions={['export_domains', 'import_domains']} mode="any">
            <ImportExportButtons
              entityType="domains"
              entityLabel="Domains"
            />
          </AuthorizationGate>
          <AuthorizationGate actions={['create_domain']}>
            <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
              <DialogTrigger asChild>
                <Button onClick={resetForm}>
                  <Plus className="w-4 h-4 mr-2" />
                  Add Domain
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Create New Domain</DialogTitle>
                  <DialogDescription>
                    Add a new IT domain to the recommendation engine
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="id">Domain ID</Label>
                    <Input
                      id="id"
                      value={formData.id}
                      onChange={(e) => setFormData({ ...formData, id: e.target.value })}
                      placeholder="e.g., security"
                    />
                  </div>
                  <div>
                    <Label htmlFor="name">Domain Name</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="e.g., IT Security"
                    />
                  </div>
                  <div>
                    <Label htmlFor="description">Description</Label>
                    <Textarea
                      id="description"
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      placeholder="Describe what this domain covers..."
                    />
                  </div>
                  <div>
                    <Label>Icon</Label>
                    <IconPicker
                      value={formData.icon}
                      onChange={(icon) => setFormData({ ...formData, icon })}
                    />
                  </div>
                  <div>
                    <Label htmlFor="pass_threshold">Pass Rate (%)</Label>
                    <Input
                      id="pass_threshold"
                      type="number"
                      min={0}
                      max={100}
                      step={5}
                      value={formData.pass_threshold}
                      onChange={(e) => setFormData({ ...formData, pass_threshold: Number(e.target.value) })}
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Minimum weighted pass rate required per maturity level (default: 70%)
                    </p>
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button onClick={handleCreate} disabled={createDomainMutation.isPending}>
                    {createDomainMutation.isPending ? 'Creating...' : 'Create'}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </AuthorizationGate>
        </div>
      </div>

      {/* Search */}
      <div className="flex items-center space-x-2">
        <div className="relative flex-1">
          <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search domains..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-8"
          />
        </div>
      </div>

      {/* Domains Cards */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <Settings className="w-5 h-5 text-gray-500" />
          <h4 className="text-sm font-semibold text-gray-700">Domains ({filteredDomains.length})</h4>
        </div>
        {filteredDomains.length > 0 ? (
          <motion.div
            className="grid grid-cols-1 gap-2"
            initial="hidden"
            animate="show"
            variants={{ hidden: {}, show: { transition: { staggerChildren: 0.06 } } }}
          >
            {filteredDomains.map((domain: Domain) => (
              <motion.div
                key={domain.id}
                variants={{ hidden: { opacity: 0, y: 16 }, show: { opacity: 1, y: 0 } }}
                transition={{ duration: 0.25 }}
              >
                <DomainCard
                  domain={domain as DomainCardData}
                  onEdit={(d) => handleEdit(d as Domain)}
                  onDelete={handleDelete}
                />
              </motion.div>
            ))}
          </motion.div>
        ) : (
          <Card>
            <CardContent className="py-12 text-center text-gray-500">
              <p className="font-medium">No domains found</p>
              <p className="text-sm mt-1">Try adjusting your search or add a new domain.</p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Edit Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Domain</DialogTitle>
            <DialogDescription>
              Update the domain information
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="edit-id">Domain ID</Label>
              <Input
                id="edit-id"
                value={formData.id}
                onChange={(e) => setFormData({ ...formData, id: e.target.value })}
                disabled
              />
            </div>
            <div>
              <Label htmlFor="edit-name">Domain Name</Label>
              <Input
                id="edit-name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="edit-description">Description</Label>
              <Textarea
                id="edit-description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
            </div>
            <div>
              <Label>Icon</Label>
              <IconPicker
                value={formData.icon}
                onChange={(icon) => setFormData({ ...formData, icon })}
              />
            </div>
            <div>
              <Label htmlFor="edit-pass_threshold">Pass Rate (%)</Label>
              <Input
                id="edit-pass_threshold"
                type="number"
                min={0}
                max={100}
                step={5}
                value={formData.pass_threshold}
                onChange={(e) => setFormData({ ...formData, pass_threshold: Number(e.target.value) })}
              />
              <p className="text-xs text-muted-foreground mt-1">
                Minimum weighted pass rate required per maturity level (default: 70%)
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleUpdate} disabled={updateDomainMutation.isPending}>
              {updateDomainMutation.isPending ? 'Updating...' : 'Update'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </motion.div>
  )
}

export default DomainManagement
